
import {
  computed,
  defineComponent,
  ref,
  onMounted,
  getCurrentInstance,
  toRef,
  WritableComputedRef,
  PropType,
  watch,
  onBeforeUnmount,
} from "vue";
import arraySort from "array-sort";
import VZFilter from "@/components/kt-datatable/components/VZFilter.vue";
import VZSort from "@/components/kt-datatable/components/VZSort.vue";
import { PAGE_ITEMS } from "@/helpers/pagination";
import AddEditModal from "@/components/modals/forms/AddEditModal.vue";
//import KTCreateEventModal from "@/components/modals/wizards/CreateEventModal.vue";
import { TranslationService } from "@/core/services/TranslationService";
import { createMicroServiceURL } from "@/helpers/microServiceMapper";
interface IPagination {
  page: number;
  total: number;
  rowsPerPage: number;
  search?: string;
}

interface IHeaderConfiguration {
  name?: string;
  key: string;
  sortingField?: string;
  sortable?: boolean;
  attrs?: Record<string, any>;
}

import {
  IBulkDelete,
  IBulkUpdate,
  IDataTableFilterOption,
} from "@/interfaces/iPageModel";
import { LookupAPI } from "@/services/api/Lookup.service";
import { useToast } from "vue-toastification";
import { IAddEditFormModel } from "@/interfaces/iAddEditFormModel";
import VZModal from "@/components/content/VZModal.vue";
import { GET_SELECTED_SITES_STR, SITE_STORE } from "@/store/userSite/types";
import store from "@/store";
import { hasPermission } from "@/core/services/permission.service";
import { Actions } from "@/store/enums/StoreEnums";
import { FormatMethods } from "@/helpers/data-format";
import { useStore } from "vuex";
import { ICurrentUser } from "@/interfaces/iCurrentUser";
export default defineComponent({
  name: "kt-datatable",
  emits: [
    "current-change",
    "sort",
    "items-per-page-change",
    "query-update",
    "table-row-click",
    "filter-change",
    "form-submit",
    "bulk-delete",
    "custom-form",
    "create-event",
    "create-form",
    "create-user-account",
    "on-site-change",
    "bulk-deleted",
    "form-cancel",
    "on-modal-show",
    "bulk-status-update",
    "show-bulk-update-modal",
    "selected-items-for-delete",
  ],
  props: {
    tableHeader: {
      type: Array as () => Array<IHeaderConfiguration>,
      required: true,
    },
    tableData: { type: Array, required: true },
    tableForm: { type: Object as PropType<IAddEditFormModel>, required: false },
    emptyTableText: { type: String, default: "No data found" },
    loading: { type: Boolean, default: false },
    currentPage: { type: Number, default: 1 },
    enableItemsPerPageDropdown: { type: Boolean, default: true },
    page: { type: Number, default: 1 },
    total: { type: Number, default: 0 },
    totalPages: { type: Number, default: 0 },
    rowsPerPage: { type: Number, default: 20 },
    order: { type: String, default: "asc" },
    sortLabel: { type: String, default: "" },
    showSearch: { type: Boolean, default: true },
    activityTab: { type: Boolean, default: false },
    defaultFilter: {},
    tableFilter: { type: Array as () => Array<IDataTableFilterOption> },
    bulkDelete: {
      type: Object as PropType<IBulkDelete>,
      required: false,
    },
    bulkStatusUpdate: {
      type: Object as PropType<IBulkUpdate>,
      required: false,
    },
    bulkDeleteTextForSections: {
      type: String,
      default: null,
    },
    bulkDeleteTextForForms: {
      type: String,
      default: null,
    },
    bulkDeleteTextForEvents: {
      type: String,
      default: null,
    },
    viewName: { type: String, default: "" },
    pageName: { type: String, default: "" },
    addButtonText: {
      type: String,
      default: () => TranslationService.translate("common.create"),
      required: false,
    },
    permissions: {
      type: Object as PropType<any>,
      default: () => {
        return {
          create: "all",
          update: "all",
          delete: "all",
        };
      },
    },
    customForm: { type: Boolean, default: false },
    cardClass: {
      type: [String, Array, Object],
      default: "",
    },
    cardHeader: {
      type: Boolean,
      default: true,
    },
  },
  components: {
    VZFilter,
    VZSort,
    "vz-add-edit-modal": AddEditModal,
    VZModal,
    //KTCreateEventModal,
  },
  setup(props, { emit }) {
    const toast = useToast();
    const store = useStore();
    const t = (key: string) => {
      return TranslationService.translate(key);
    };
    const currentUser = computed(() => {
      return store.getters.currentUser as ICurrentUser;
    });
    const data = toRef(props, "tableData");
    const tableHeader = toRef(props, "tableHeader");
    const loading = toRef(props, "loading");
    const tableHeaders = ref<IHeaderConfiguration[]>([]);
    const currentSort = ref<string>("");
    const localCurrentSort = ref<string>("");
    const searchTerm = ref<string>("");
    const order = ref(props.order);
    const permission = ref(props.permissions);
    const label = ref(props.sortLabel);
    const total = toRef(props, "total");
    const rowsPerPage = toRef(props, "rowsPerPage");
    const tableFilter = toRef(props, "tableFilter");
    const page = toRef(props, "page");
    const currentFilter = ref({});
    const isFiltering = ref<boolean>(false);
    const showDialog = ref<any>(false);
    const deleteBtnLoading = ref<boolean>(false);
    let tableWidth = ref(1200);
    const VZFilter = ref<null | HTMLFormElement>(null);
    const selectedIndexOfRow = ref<number>();
    const defaultFilter = toRef<Record<string, any>, any>(
      props,
      "defaultFilter"
    );
    const selectedForDelete = ref<string[]>([]);
    const bulkDelete = toRef(props, "bulkDelete");
    const bulkStatusUpdate = toRef(props, "bulkStatusUpdate");
    const tableFormData = toRef(props, "tableForm");
    const tableFormDataObj = ref(
      computed(() => {
        return tableFormData.value;
      }).value
    );
    const customForm = toRef(props, "customForm");
    const pagination = ref<IPagination>({
      page: page.value || 1,
      total: total.value,
      rowsPerPage: rowsPerPage.value,
    });
    const options = toRef(props, "tableFilter");
    const PAGE_ITEM = ref<number[]>(PAGE_ITEMS);
    const viewName = toRef(props, "viewName");
    const pageName = toRef(props, "pageName");
    const vnodeProps = getCurrentInstance()?.vnode.props || {};
    const multipleDeleteSuccessMessage = t(
      "common.multipleDeleteSuccessMessage"
    );
    const openAddEditModal = ref<boolean>(false);
    const initialRowsPerPage = ref<number>(rowsPerPage.value);
    const localPagination: WritableComputedRef<IPagination> = computed({
      get(): IPagination {
        return {
          page: page.value,
          total: total.value,
          rowsPerPage: rowsPerPage.value,
        };
      },
      set(newValue: IPagination): void {
        updateQuery(newValue);
      },
    });
    const checkAll: WritableComputedRef<boolean> = computed({
      get(): boolean {
        return (
          (bulkDelete.value?.showDelete &&
            getItems.value.length > 0 &&
            getItems.value.every((item: any) => {
              return (
                item &&
                bulkDelete.value?.key &&
                selectedForDelete.value.includes(item[bulkDelete.value?.key])
              );
            })) ??
          false
        );
      },
      set(newValue: boolean): void {
        const localSelect = getItems.value.map(
          (item: any) =>
            (bulkDelete.value?.key && item[bulkDelete.value?.key]) ?? ""
        );
        if (newValue) {
          selectedForDelete.value = [
            ...selectedForDelete.value,
            ...localSelect.filter((c) => !selectedForDelete.value.includes(c)),
          ];
        } else {
          selectedForDelete.value = [
            ...selectedForDelete.value.filter((c) => !localSelect.includes(c)),
          ];
        }
      },
    });

    const getTooltipVal = (key, value) => {
      if (key == "rolePermissionModels") {
        return value
          .map((val) => {
            return val.roleName;
          })
          .toString();
      }

      if (value && key.toLowerCase().includes("date")) {
        return FormatMethods.convertUTCToLocalDate(value, "MM-DD-YYYY hh:mm A");
      }
      return value;
    };
    const getShowDelete = computed(() => {
      return (
        (bulkDelete.value?.showDelete &&
          getItems.value.length &&
          getItems.value.every((item: any) => {
            return (
              item &&
              bulkDelete.value?.key &&
              Object.keys(item) &&
              Object.keys(item).includes(bulkDelete.value?.key)
            );
          })) ??
        false
      );
    });
    const getSortOptions = computed(() => {
      return tableHeader.value.filter((item) => item?.sortable) ?? [];
    });
    const selectedSites = computed(() => {
      const key = `${SITE_STORE}/${GET_SELECTED_SITES_STR}`;
      return store.getters[key] as string;
    });
    const bulkDeleteText = computed(() => {
      if (bulkDelete.value?.entity) {
        return `${t("validation.sureToDeleteSelected")} ${
          bulkDelete.value?.entityLabel?.toLowerCase() ?? ""
        }?`;
      }
      return `${t("validation.sureToDeleteSelected")} items?`;
    });
    onMounted(() => {
      localCurrentSort.value = label.value + order.value;
      tableWidth.value =
        document?.getElementById(`vz_table_${viewName.value}`)?.offsetWidth ??
        1200;
      window.addEventListener(
        "resize",
        function () {
          tableWidth.value =
            document?.getElementById(`vz_table_${viewName.value}`)
              ?.offsetWidth ?? 1200;
        },
        true
      );
      currentFilter.value = { ...defaultFilter.value };
      // capture initial rowsPerPage one time for reset behavior
      initialRowsPerPage.value = rowsPerPage.value;
    });

    onBeforeUnmount(() => {
      window.removeEventListener("resize", function () {
        tableWidth.value =
          document?.getElementById(`vz_table_${viewName.value}`)?.offsetWidth ??
          1200;
      });
    });
    const tabelHeaderCallOnUpdate = () => {
      // setTimeout(() => {
      tableHeaders.value = computed(() => {
        return tableHeader.value.map((col) => {
          return col.key?.toLowerCase() == "actions" &&
            hasPermission(props.permissions.delete) == false &&
            hasPermission(props.permissions.update) == false
            ? ({} as IHeaderConfiguration)
            : col;
        });
      }).value;
      // }, 500);
    };

    const getItems = computed(() => {
      return data.value;
    });

    const currentPageChange = (val) => {
      localPagination.value = { ...localPagination.value, page: val };
      emit("current-change", val);
    };
    const getSortKey = (header: IHeaderConfiguration) => {
      const { key, sortingField } = header;
      return sortingField || key;
    };

    const sort = (header: IHeaderConfiguration, sortable) => {
      const columnName = getSortKey(header);
      if (sortable === false) {
        return;
      }

      const compareFunction = (a, b) => {
        if (
          typeof a[columnName] === "string" &&
          typeof b[columnName] === "string"
        ) {
          return a[columnName].localeCompare(b[columnName]);
        }
        return a[columnName] - b[columnName];
      };

      if (order.value === "asc") {
        order.value = "desc";
        data.value.sort((a, b) => compareFunction(b, a));
      } else {
        order.value = "asc";
        data.value.sort(compareFunction);
      }

      if ("onSort" in vnodeProps) {
        emit("sort", { columnName: columnName, order: order.value });
      }

      localCurrentSort.value = columnName + order.value;
    };

    const setItemsPerPage = (event) => {
      emit("items-per-page-change", parseInt(event.target.value));
      localPagination.value = {
        ...localPagination.value,
        page: 1,
        rowsPerPage: parseInt(event.target.value),
      };
    };
    const search = () => {
      if (searchTerm.value.length >= 3) {
        localPagination.value = {
          ...localPagination.value,
          page: 1,
        };
      } else if (searchTerm.value.length == 0) {
        localPagination.value = {
          ...localPagination.value,
          page: 1,
        };
      }
    };
    const filterChange = (filters, isUserApply?, isReset?) => {
      const localFilters = Object.keys(filters)
        .filter((key) => {
          const lookup =
            (tableFilter.value &&
              tableFilter.value.findIndex(
                (tab) => tab.key === key && tab.type === "lookup"
              )) ??
            -1;
          if (lookup > -1) {
            return !filters[key].includes("-1");
          }
          return true;
        })
        .reduce((filter, key) => {
          filter[key] = filters[key];
          return filter;
        }, {});
      currentFilter.value = { ...localFilters };
      // Only consider manual Apply as filtering when there is at least one non-empty value
      const hasAnyValue = Object.keys(localFilters).some((k) => {
        const v = (localFilters as any)[k];
        if (Array.isArray(v)) return v.length > 0;
        if (v === undefined || v === null) return false;
        return `${v}`.trim() !== "";
      });
      isFiltering.value = !!isUserApply && hasAnyValue;
      if (isReset) {
        isFiltering.value = false;
      }

      // Build query with current filters
      const newRowsPerPage = isReset
        ? initialRowsPerPage.value
        : rowsPerPage.value;
      const newPage = 1;
      const maxPage = Math.floor((total.value || 0) / (newRowsPerPage || 1));
      const safePage = Math.max(Math.min(newPage - 1, maxPage), 0);

      const tableQuery = {
        ...currentFilter.value,
        page: safePage,
        size: newRowsPerPage,
        search: searchTerm.value?.trim(),
        sort: currentSort.value,
      };

      // Emit query-update with filters already applied
      emit("query-update", tableQuery);
    };
    const sortChange = (sort) => {
      currentSort.value = sort;
      localPagination.value = {
        ...localPagination.value,
        page: 1,
      };
    };
    const applyBulkDelete = async () => {
      try {
        if (bulkDelete.value?.entity && selectedForDelete.value) {
          deleteBtnLoading.value = true;
          let deletedMsg;
          if (
            viewName.value === "userAccounts" ||
            pageName.value === "siteUser"
          ) {
            deletedMsg =
              "Selected user(s)" + " " + multipleDeleteSuccessMessage;
          } else if (viewName.value == "formUsersTable") {
            deletedMsg =
              "Selected user/role(s)" + " " + multipleDeleteSuccessMessage;
          } else if (
            viewName.value === "userSites" &&
            pageName.value != "siteUser"
          ) {
            deletedMsg =
              "Selected branch(s)" + " " + multipleDeleteSuccessMessage;
          } else if (viewName.value == "sites") {
            deletedMsg =
              "Selected branch(s)" + " " + multipleDeleteSuccessMessage;
          } else if (viewName.value == "formUsers") {
            deletedMsg =
              "Selected form(s)" + " " + multipleDeleteSuccessMessage;
          } else if (viewName.value == "lookup") {
            deletedMsg =
              "Selected lookup(s)" + " " + multipleDeleteSuccessMessage;
          } else if (viewName.value == "lookupsData") {
            deletedMsg =
              "Selected lookup data(s)" + " " + multipleDeleteSuccessMessage;
          } else if (viewName.value == "customPermissions") {
            deletedMsg =
              "Selected custom permission(s)" +
              " " +
              multipleDeleteSuccessMessage;
          } else {
            deletedMsg =
              "Selected " +
              viewName.value.slice(0, -1) +
              "(s) " +
              multipleDeleteSuccessMessage;
          }
          if ("onBulkDelete" in vnodeProps) {
            emit("bulk-delete", selectedForDelete.value);
          } else {
            await LookupAPI.getInstance().bulkDeleteEntity(
              createMicroServiceURL(bulkDelete.value?.entity),
              selectedForDelete.value
            );
            localPagination.value = {
              ...localPagination.value,
              page: 1,
            };
            emit("bulk-deleted", selectedForDelete.value);
            toast.success(deletedMsg);
          }
          showDialog.value = false;
        }
      } finally {
        deleteBtnLoading.value = false;
      }
    };
    const applyBulkUpdate = async () => {
      try {
        if (bulkStatusUpdate.value?.entity && selectedForDelete.value) {
          emit("bulk-status-update", selectedForDelete.value);
        }
      } finally {
        //
      }
    };
    const cancel = async () => {
      // tableForm.value.edit = false;
      openAddEditModal.value = false;
      emit("form-cancel");
    };

    const submit = async (reqData) => {
      if (viewName.value != "sites") {
        openAddEditModal.value = false;
      }
      emit("form-submit", { ...reqData });
    };
    const createForm = async (option?) => {
      if (tableFormData.value && customForm.value == false) {
        tableFormData.value.edit = false;
        tableFormData.value.data = {};
        openAddEditModal.value = true;
        emit("on-modal-show");
      } else {
        emit("custom-form");
      }
    };
    const updateQuery = (pagination) => {
      const maxPage = Math.floor(
        (pagination.total || 0) / (pagination.rowsPerPage || 1)
      );
      const safePage = Math.max(Math.min(pagination.page - 1, maxPage), 0);

      const {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        total = 0,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        rowsPerPage = 0,
        ...tableQuery
      } = {
        ...pagination,
        ...currentFilter.value,
        page: safePage,
        size: pagination.rowsPerPage,
        search: searchTerm.value?.trim(),
        sort: currentSort.value,
      };
      localCurrentSort.value = "";
      emit("query-update", tableQuery);
    };
    const handelSelectedRowItem = async (item, i) => {
      if (viewName.value == "userRole") {
        selectedIndexOfRow.value = i;
      }
      emit("table-row-click", item);
    };
    watch(
      data,
      () => {
        selectedForDelete.value = [];
        tabelHeaderCallOnUpdate();
        document.getElementsByClassName("modal-body")[0].scrollTop = 0;
      },
      { deep: true }
    );

    watch(selectedSites, () => {
      localPagination.value = {
        ...localPagination.value,
        page: 1,
      };

      document.getElementsByClassName("modal-body")[0].scrollTop = 0;
    });

    watch(
      tableFormData,
      (newValue) => {
        tableFormDataObj.value = newValue;

        document.getElementsByClassName("modal-body")[0].scrollTop = 0;
      },
      { deep: true }
    );
    watch(loading, (newValue) => {
      if (newValue) {
        selectedForDelete.value = [];
        store.dispatch(Actions.ADD_BODY_CLASSNAME, "page-loading");
      } else {
        store.dispatch(Actions.REMOVE_BODY_CLASSNAME, "page-loading");
      }
      document.getElementsByClassName("modal-body")[0].scrollTop = 0;
    });
    const cannotUnassignAll = t("pages.site.cannotUnassignAll");
    const cannotBeUnassign = t("pages.site.users.cannotBeUnassign");
    const bulkDeleteLockedLookups = t("pages.lookup.bulkDeleteLockedLookups");
    const roles =
      currentUser.value?.currentUserSite?.map((role) => role.roleName) || [];
    const isAdmin = roles.includes("Admin");
    const showBulkDeleteModal = () => {
      if (
        viewName.value == "sections" ||
        viewName.value == "forms" ||
        viewName.value == "events"
      ) {
        emit("selected-items-for-delete", selectedForDelete.value);
        showDialog.value = true;
      } else if (
        viewName.value == "userSites" &&
        pageName.value == "siteUser"
      ) {
        const isCurrentUserSelected = getItems.value.some(
          (item: any) =>
            selectedForDelete.value.includes(item.webId) &&
            item.userAccountId === currentUser.value.webId
        );

        if (
          !isAdmin &&
          currentUser.value?.currentUserSite?.length === 1 &&
          isCurrentUserSelected
        ) {
          toast.error(cannotBeUnassign);
        } else {
          showDialog.value = true;
        }
      } else if (viewName.value == "userSites" && pageName.value == "") {
        if (!isAdmin && data.value.length === selectedForDelete.value.length) {
          toast.error(cannotUnassignAll);
        } else {
          showDialog.value = true;
        }
      } else if (viewName.value == "lookup") {
        const hasLocked = getItems.value.some(
          (item: any) =>
            selectedForDelete.value.includes(item.webId) && item.lock === true
        );

        if (hasLocked) {
          toast.error(bulkDeleteLockedLookups);
        } else {
          showDialog.value = true;
        }
      } else {
        showDialog.value = true;
      }
    };
    return {
      currentUser,
      pagination,
      getShowDelete,
      selectedForDelete,
      currentPageChange,
      getItems,
      sort,
      currentSort,
      setItemsPerPage,
      data,
      localPagination,
      search,
      searchTerm,
      options,
      filterChange,
      getSortOptions,
      checkAll,
      applyBulkDelete,
      PAGE_ITEM,
      submit,
      sortChange,
      cancel,
      tableFormData,
      t,
      tableWidth,
      deleteBtnLoading,
      updateQuery,
      createForm,
      localCurrentSort,
      permission,
      hasPermission,
      tableHeaders,
      getSortKey,
      tableFormDataObj,
      openAddEditModal,
      bulkDeleteText,
      showDialog,
      getTooltipVal,
      applyBulkUpdate,
      VZFilter,
      handelSelectedRowItem,
      selectedIndexOfRow,
      showBulkDeleteModal,
    };
  },
});
